import { useState } from "react";
import { Pode } from "@/lib/permissoes";
import { useEstadoNaUrl } from "@/lib/estado-na-url";
import { useNavegacao } from "@/lib/navegacao";
import { Button } from "@/components/ui/button";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { MessageCircle, Pencil, Megaphone, ExternalLink, Inbox } from "lucide-react";
import { PageHeader } from "@/components/layout/page-header";
import { Segmented } from "@/components/layout/segmented";
import { StatCard, StatCardGrid } from "@/components/data/stat-card";
import { PageSkeleton } from "@/components/data/page-skeleton";
import { EmptyState } from "@/components/data/empty-state";
import { useCompradorDetalhe, type FiltroDetalheCorretor } from "@/hooks/use-mercado";
import { ProdutorShell } from "@/components/mercado/produtor-shell";
import { StatusChip } from "@/components/mercado/status-chip";
import { ChipFormalizacao } from "@/components/mercado/formalizacao-chip";
import { ChipDinheiro } from "@/components/mercado/dinheiro-chip";
import { dinheiroEmAberto } from "@shared/mercado";
import { StackTable, StackRow, StackCell } from "@/components/mercado/stack-table";
import { DialogComprador } from "@/components/mercado/dialog-comprador";
import { DialogRecebi } from "@/components/mercado/dialog-recebi";
import { fmtMoedaBR, fmtKgBR, fmtDataBR, fmtToneladas } from "@/lib/formato-mercado";
import type { RomaneioDoCompradorDTO } from "@shared/mercado";

type Pagamento = NonNullable<FiltroDetalheCorretor["pagamento"]>;

/**
 * Corretor · detalhe: os romaneios formalizados com ele, por ano e situação.
 * Tiles só com número que existe — prazo médio some quando não há base, e o
 * desvio de conferência só nasce no C3.
 */
// ⚠️ Constantes de MÓDULO (deps de useMemo dentro do hook).
// `editar` NÃO entra: é parâmetro de ENTRADA (o painel de envio da oferta manda
// `?editar=telefone`), consumido uma vez na montagem — não descreve a lista.
const PADRAO_DETALHE = { pagamento: "aberto", ano: "todos" };
const PERMITIDOS_DETALHE = { pagamento: ["aberto", "pagos", "todos"] } as const;

export default function MercadoCompradorDetalhe({ compradorId }: { compradorId: string }) {
  const { voltar } = useNavegacao();
  const { estado: filtros, definir } = useEstadoNaUrl(PADRAO_DETALHE, PERMITIDOS_DETALHE);
  const pagamento = filtros.pagamento as Pagamento;
  const ano = filtros.ano;
  /**
   * `?editar=telefone` abre a edição já com o cursor no número — é como o
   * painel de envio da oferta manda quem está sem telefone para cá. Sem isso,
   * o produtor chegaria numa tela de leitura e teria de achar o "Editar".
   */
  const focarTelefone = typeof window !== "undefined"
    && new URLSearchParams(window.location.search).get("editar") === "telefone";
  const [editar, setEditar] = useState(focarTelefone);
  const [recebi, setRecebi] = useState<RomaneioDoCompradorDTO | null>(null);

  const { data, isLoading, error } = useCompradorDetalhe(compradorId, {
    pagamento,
    ano: ano === "todos" ? undefined : Number(ano),
  });

  if (isLoading) {
    return <ProdutorShell><PageSkeleton variante="painel" /></ProdutorShell>;
  }
  if (error || !data) {
    return (
      <ProdutorShell>
        <EmptyState
          icone={Inbox}
          titulo="Comprador não encontrado"
          descricao="Ele pode ter sido removido, ou o link está errado."
          acao={<Button variant="outline" onClick={() => voltar("/mercado/compradores")}>Voltar para Compradores</Button>}
        />
      </ProdutorShell>
    );
  }

  /**
   * "kg conferido" só existe quando ALGUÉM conferiu. Até o C3, `kgConferido` é
   * `null` em todo romaneio: uma coluna inteira de "—" ocupa ~80px, empurra a
   * tabela para além do espaço disponível e não informa nada. Ela volta sozinha
   * quando a conferência chegar — a condição é o dado, não uma flag de ciclo.
   */
  const mostrarConferido = data.romaneios.some(r => r.kgConferido !== null);

  const waLink = data.telefoneWhatsapp ? `https://wa.me/55${data.telefoneWhatsapp}` : null;
  const chipEstado = data.estado === "ATIVO"
    ? <StatusChip kind="pago">Ativo</StatusChip>
    : data.estado === "CONVIDADO" ? <StatusChip kind="warn">Convidado</StatusChip> : <StatusChip kind="aberta">Sem acesso</StatusChip>;

  return (
    <ProdutorShell>
      <PageHeader
        breadcrumb={
          <button type="button" className="hover:text-foreground" onClick={() => voltar("/mercado/compradores")}>Compradores</button>
        }
        titulo={data.nome}
        descricao={[data.telefoneMascarado, data.cidade].filter(Boolean).join(" · ") || undefined}
        empilharQuandoEstreito
        acoesSecundarias={
          <>
            {waLink ? (
              <Button variant="outline" asChild>
                <a href={waLink} target="_blank" rel="noopener noreferrer"><MessageCircle className="h-4 w-4 mr-1.5" />WhatsApp</a>
              </Button>
            ) : (
              <Button variant="outline" disabled title="Sem telefone cadastrado"><MessageCircle className="h-4 w-4 mr-1.5" />WhatsApp</Button>
            )}
            <Button variant="outline" onClick={() => setEditar(true)} data-testid="button-editar-comprador">
              <Pencil className="h-4 w-4 mr-1.5" />Editar
            </Button>
          </>
        }
        acao={
          <Button disabled title="Ofertar carga chega com as Cotações">
            <Megaphone className="h-4 w-4 mr-1.5" />Ofertar carga
          </Button>
        }
      />

      <div className="flex items-center gap-2">
        {chipEstado}
        {/* "conferiu N de M" entra no C3 — até lá `conferiu` é null e nada é inventado. */}
      </div>

      <StatCardGrid>
        <StatCard rotulo="Em aberto" valor={fmtMoedaBR(data.kpis.emAberto)} testId="tile-em-aberto"
          legenda={data.kpis.emAberto > 0 ? <StatusChip kind="warn">a receber</StatusChip> : undefined} />
        <StatCard rotulo="Comprado no ano" valor={fmtMoedaBR(data.kpis.compradoAno)} legenda={fmtToneladas(data.kpis.volumeAno)} testId="tile-comprado-ano" />
        {data.kpis.prazoMedioDias !== null && (
          <StatCard
            rotulo="Prazo médio de pagamento"
            valor={`${data.kpis.prazoMedioDias > 0 ? "+" : ""}${data.kpis.prazoMedioDias.toLocaleString("pt-BR", { maximumFractionDigits: 1 })} d`}
            legenda={data.kpis.prazoMedioDias > 0 ? "depois do vencimento" : "antes do vencimento"}
            testId="tile-prazo-medio"
          />
        )}
        {/* Desvio na conferência: oculto até o C3. */}
      </StatCardGrid>

      <div className="flex flex-col gap-2 md:flex-row md:items-center md:justify-between">
        <Segmented<Pagamento>
          valor={pagamento}
          onChange={v => definir({ pagamento: v })}
          testId="seg-pagamento"
          className="w-full md:w-auto [&>button]:flex-1 md:[&>button]:flex-none"
          opcoes={[
            { id: "aberto", label: "Em aberto" },
            { id: "pagos", label: "Pagos" },
            { id: "todos", label: "Todos" },
          ]}
        />
        <Select value={ano} onValueChange={v => definir({ ano: v })}>
          <SelectTrigger className="h-9 w-full md:w-[150px]" data-testid="select-ano"><SelectValue /></SelectTrigger>
          <SelectContent>
            <SelectItem value="todos">Todos os anos</SelectItem>
            {data.anos.map(a => <SelectItem key={a} value={String(a)}>{a}</SelectItem>)}
          </SelectContent>
        </Select>
      </div>

      {data.romaneios.length === 0 ? (
        <EmptyState
          icone={Inbox}
          compacto
          titulo="Nenhum romaneio neste recorte"
          descricao="Formalize uma venda emitida no Cacho e ela aparece aqui. Ou troque o filtro."
        />
      ) : (
        <StackTable
          testId="tabela-romaneios-comprador"
          // Sete colunas sem conferência (oito com ela): abaixo disto a tabela
          // seria cortada, e cartão legível é melhor do que tabela sem a coluna
          // de ações. O limiar acompanha a coluna que aparece ou some.
          larguraMinima={mostrarConferido ? 780 : 700}
          colunas={[
            { label: "Romaneio", w: true },
            { label: "kg", num: true },
            ...(mostrarConferido ? [{ label: "kg conferido", num: true }] : []),
            { label: "Valor", num: true },
            { label: "Vencimento" },
            { label: "Formalização" },
            { label: "Pagamento" },
            { label: "" },
          ]}
          rodape={
            <tr>
              <td className="w">{data.totais.romaneios} {data.totais.romaneios === 1 ? "romaneio" : "romaneios"}</td>
              <td className="num" data-label="kg">{fmtKgBR(data.totais.kg)}</td>
              {mostrarConferido && <td />}
              <td className="num" data-label="Valor">{fmtMoedaBR(data.totais.valor)}</td>
              <td /><td /><td /><td />
            </tr>
          }
        >
          {data.romaneios.map(r => (
            <LinhaRomaneio key={r.vendaId} r={r} compradorNome={data.nome} mostrarConferido={mostrarConferido} onRecebi={() => setRecebi(r)} />
          ))}
        </StackTable>
      )}

      {editar && (
        <DialogComprador
          comprador={{ id: data.compradorId, nome: data.nome, telefone: data.telefoneWhatsapp, cidade: data.cidade, perfil: data.perfil }}
          focarTelefone={focarTelefone}
          onFechar={() => setEditar(false)}
        />
      )}
      {recebi && (
        <DialogRecebi
          romaneios={[{ vendaId: recebi.vendaId, numero: recebi.numero, comprador: data.nome, valor: recebi.aReceber, dataVenda: recebi.data }]}
          inicialId={recebi.vendaId}
          onFechar={() => setRecebi(null)}
        />
      )}
    </ProdutorShell>
  );
}

function diasAte(iso: string): number {
  const hoje = new Date();
  const a = Date.UTC(hoje.getFullYear(), hoje.getMonth(), hoje.getDate());
  const b = Date.UTC(Number(iso.slice(0, 4)), Number(iso.slice(5, 7)) - 1, Number(iso.slice(8, 10)));
  return Math.round((b - a) / 86_400_000);
}

function LinhaRomaneio({ r, compradorNome, mostrarConferido, onRecebi }: {
  r: RomaneioDoCompradorDTO;
  compradorNome: string;
  /** Ver `mostrarConferido` na página: sem conferência, a coluna não existe. */
  mostrarConferido: boolean;
  onRecebi: () => void;
}) {
  const { abrir } = useNavegacao();
  // Do servidor: A RECEBER, PARCIAL e CONTESTADO são "o dinheiro não entrou todo".
  const aberto = dinheiroEmAberto(r.dinheiro);
  const dias = r.vencimento ? diasAte(r.vencimento) : null;
  return (
    <StackRow testId={`romaneio-${r.vendaId}`}>
      <StackCell w>
        <span className="font-semibold">Nº {r.numero}</span>
        <span className="block text-xs text-muted-foreground">{fmtDataBR(r.data)}{r.caixas > 0 && <> · {r.caixas} cx</>}</span>
      </StackCell>
      <StackCell label="kg" num>{fmtKgBR(r.kg)}</StackCell>
      {mostrarConferido && (
        <StackCell label="kg conferido" num>{r.kgConferido === null ? "—" : fmtKgBR(r.kgConferido)}</StackCell>
      )}
      <StackCell label="Valor" num><span className="font-semibold">{fmtMoedaBR(r.valor)}</span></StackCell>
      <StackCell label="Vencimento">
        {r.vencimento ? (
          <>
            {fmtDataBR(r.vencimento)}
            {aberto && dias !== null && (
              <span className={`block text-[11px] ${dias <= 3 ? "text-atencao font-semibold" : "text-muted-foreground"}`}>
                {dias < 0 ? `venceu há ${Math.abs(dias)} d` : dias === 0 ? "vence hoje" : `em ${dias} d`}
              </span>
            )}
          </>
        ) : "—"}
      </StackCell>
      <StackCell label="Formalização">
        <ChipFormalizacao formalizacao={r.formalizacao} compradorNome={compradorNome} emAberto={aberto} testIdSufixo={r.vendaId} />
      </StackCell>
      <StackCell label="Pagamento"><ChipDinheiro estado={r.dinheiro} testIdSufixo={r.vendaId} /></StackCell>
      <StackCell acts>
        <Button variant="outline" size="sm" onClick={() => abrir(`/cacho/vendas/${r.vendaId}`)} data-testid={`button-abrir-venda-${r.vendaId}`}>
          Abrir <ExternalLink className="h-3.5 w-3.5 ml-1" />
        </Button>
        {aberto && (
          <Pode cap="mercado.receber"><Button size="sm" onClick={onRecebi} data-testid={`button-recebi-${r.vendaId}`}>Recebi</Button></Pode>
        )}
      </StackCell>
    </StackRow>
  );
}
